import { Action, combineReducers, createReducer, on } from '@ngrx/store';

import { generateHistoricalPrescriptionsByDate } from '../utils/generate-historical-prescriptions.function';
import { addPrescriptionDraft, clearPrescriptionDrafts, updatePrescriptionDraft } from './prescriptions-drafts.actions';
import { prescriptionsDraftsReducer } from './prescriptions-drafts.reducer';
import {
  clearPrescriptions,
  clearTargetPrescription,
  deletePrescription,
  editPrescription,
  fetchPrescriptions,
  fetchPrescriptionsFailure,
  fetchPrescriptionsSuccess,
  incrementPrescriptionsPage,
  processPrescriptionsDrafts,
  processPrescriptionsDraftsFailure,
  processPrescriptionsDraftsSuccess,
  resetPrescriptionsState,
  savePrescription,
  setPrescriptionsInitRequired,
  setPrescriptionsPage,
  setPrescriptionsSearchFilter,
  setTargetPrescription,
} from './prescriptions.actions';
import { PrescriptionsFeatureState, initialPrescriptionsState } from './prescriptions.state';

const prescriptionsReducer = createReducer(
  initialPrescriptionsState,
  on(savePrescription, state => ({ ...state, prescriptionSaved: true })),
  on(editPrescription, (state, { editData }) => ({ ...state, prescriptionEdited: editData })),
  on(deletePrescription, (state, { deletionData }) => ({ ...state, prescriptionDeleted: deletionData })),
  on(setPrescriptionsInitRequired, (state, { initRequired }) => ({ ...state, initRequired })),
  on(setPrescriptionsSearchFilter, (state, { search }) => ({ ...state, guaranteesFilters: { ...state.guaranteesFilters, search } })),
  on(setPrescriptionsPage, (state, { page }) => ({ ...state, page })),
  on(incrementPrescriptionsPage, state => ({ ...state, page: state.page + 1, guaranteesFetching: true })),
  on(fetchPrescriptions, state => ({ ...state, loading: true, guaranteesFetching: true })),
  on(fetchPrescriptionsSuccess, (state, { pagedResponse }) => ({
    ...state,
    count: pagedResponse.count,
    page: pagedResponse.page,
    pageSize: pagedResponse.pageSize,
    hasNextPage: pagedResponse.hasNextPage,
    hasPreviousPage: pagedResponse.hasPreviousPage,
    guarantees: [...state.guarantees, ...generateHistoricalPrescriptionsByDate(pagedResponse)],
    loading: false,
    guaranteesFetching: false,
  })),
  on(fetchPrescriptionsFailure, state => ({ ...state, loading: false, guaranteesFetching: false })),
  on(addPrescriptionDraft, state => ({ ...state, prescriptionDraftSubmitted: false })),
  on(updatePrescriptionDraft, state => ({ ...state, prescriptionDraftSubmitted: false })),
  on(processPrescriptionsDrafts, state => ({ ...state, loading: true, draftsProcessing: true })),
  on(processPrescriptionsDraftsSuccess, state => ({ ...state, loading: false, draftsProcessing: false })),
  on(processPrescriptionsDraftsFailure, state => ({ ...state, loading: false, draftsProcessing: false })),
  on(clearPrescriptions, state => ({
    ...state,
    count: initialPrescriptionsState.count,
    page: initialPrescriptionsState.page,
    pageSize: initialPrescriptionsState.pageSize,
    hasNextPage: initialPrescriptionsState.hasNextPage,
    hasPreviousPage: initialPrescriptionsState.hasPreviousPage,
    guarantees: initialPrescriptionsState.guarantees,
    loading: initialPrescriptionsState.loading,
    guaranteesFetching: initialPrescriptionsState.guaranteesFetching,
  })),
  on(clearPrescriptionDrafts, state => state),
  on(setTargetPrescription, (state, { prescriptionId }) => ({ ...state, targetPrescription: { prescriptionId } })),
  on(clearTargetPrescription, state => ({ ...state, targetPrescription: null })),
  on(resetPrescriptionsState, () => initialPrescriptionsState)
);

const combinedReducer = combineReducers<PrescriptionsFeatureState>({
  prescriptions: prescriptionsReducer,
  drafts: prescriptionsDraftsReducer,
});

export function prescriptionsFeatureReducer(state: PrescriptionsFeatureState | undefined, action: Action): PrescriptionsFeatureState {
  if (action.type === resetPrescriptionsState.type) {
    return combinedReducer(undefined, action);
  }
  return combinedReducer(state, action);
}
